import Image from 'next/image';

import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LanguageIcon from '@mui/icons-material/Language';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { keyframes } from '@emotion/react';

import samaxLogo from '@/public/logo-radar-da-nuvem.png';
import { AuthGate } from '@/config/auth';
import { PERMISSION_USERS_BACKOFFICE_GLOBAL } from '@/config/permissions';

import { SidebarButton, SidebarGroup } from './sidebar';

const floatUp = keyframes`
  0%   { transform: translateY(0); opacity: 0; }
  15%  { opacity: 0.5; }
  85%  { opacity: 0.4; }
  100% { transform: translateY(-110vh); opacity: 0; }
`;

const pulseBlob = keyframes`
  0%, 100% { transform: scale(1);   opacity: 0.2; }
  50%      { transform: scale(1.3); opacity: 0.4; }
`;

const pulseBlobAlt = keyframes`
  0%, 100% { transform: scale(1.2); opacity: 0.15; }
  50%      { transform: scale(0.9); opacity: 0.3; }
`;

const SIDEBAR_ORBS = [
  { size: 5,  left: '10%', delay: '0s',   dur: '16s', color: 'hsl(275, 65%, 72%)' },
  { size: 8,  left: '30%', delay: '2.0s', dur: '12s', color: 'hsl(280, 70%, 68%)' },
  { size: 4,  left: '55%', delay: '0.8s', dur: '18s', color: 'hsl(270, 60%, 75%)' },
  { size: 7,  left: '75%', delay: '3.2s', dur: '14s', color: 'hsl(285, 55%, 70%)' },
  { size: 10, left: '20%', delay: '1.5s', dur: '11s', color: 'hsl(272, 75%, 65%)' },
  { size: 4,  left: '65%', delay: '2.8s', dur: '19s', color: 'hsl(268, 50%, 78%)' },
  { size: 6,  left: '45%', delay: '0.4s', dur: '15s', color: 'hsl(278, 68%, 71%)' },
  { size: 9,  left: '85%', delay: '1.8s', dur: '13s', color: 'hsl(276, 72%, 66%)' },
];

export const DrawerContents = ({ isOpen, toggleSidebar, DrawerHeader }) => (
  <Box sx={{ position: 'relative', height: '100%', px: '16px' }}>
    {isOpen && (
      <>
        <Box
          sx={{
            position: 'absolute',
            top: '-15%',
            left: '-40%',
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'rgba(115, 34, 195, 0.18)',
            filter: 'blur(80px)',
            pointerEvents: 'none',
            animation: `${pulseBlob} 10s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '5%',
            right: '-30%',
            width: 260,
            height: 260,
            borderRadius: '50%',
            background: 'rgba(140, 60, 221, 0.12)',
            filter: 'blur(100px)',
            pointerEvents: 'none',
            animation: `${pulseBlobAlt} 12s ease-in-out infinite`,
          }}
        />
        {SIDEBAR_ORBS.map((orb, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              bottom: -10,
              left: orb.left,
              width: orb.size,
              height: orb.size,
              borderRadius: '50%',
              background: orb.color,
              filter: 'blur(1px)',
              pointerEvents: 'none',
              animation: `${floatUp} ${orb.dur} linear infinite`,
              animationDelay: orb.delay,
              opacity: 0,
            }}
          />
        ))}
      </>
    )}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <DrawerHeader sx={{ padding: '8px !important' }}>
        <IconButton disableRipple onClick={toggleSidebar} color='inherit'>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
            gap={2}
          >
            {isOpen ? (
              <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
                <MenuOpenIcon sx={{ marginRight: '14px' }} />
                <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                  <Image
                    src={samaxLogo}
                    alt='Radar Da Nuvem Logo'
                    height={25}
                  />
                </Stack>
              </Stack>
            ) : (
              <Box>
                <MenuOpenIcon sx={{ transform: 'rotate(180deg)' }} />
              </Box>
            )}
          </Box>
        </IconButton>
      </DrawerHeader>
      <SidebarButton name='Início' Icon={LanguageIcon} path='/' />
      <SidebarButton name='Pesquisa' Icon={AssignmentIcon} path='/pesquisa' />
      <SidebarButton name='Benchmark' Icon={TrendingUpIcon} path='/benchmark' />
      <SidebarGroup name='Comparador' Icon={CompareArrowsIcon}>
        <SidebarButton name='Cloud' Icon={CloudOutlinedIcon} path='/comparador/diagnostico' />
        <SidebarButton name='Modelos de IA' Icon={AutoAwesomeOutlinedIcon} path='/comparador/ia/modelos' />
      </SidebarGroup>
      <SidebarButton name='Convites' Icon={AccountCircleIcon} path='/invites' />
      <AuthGate permissions={[PERMISSION_USERS_BACKOFFICE_GLOBAL]}>
        <SidebarButton name='Backoffice' Icon={AdminPanelSettingsIcon} path='/backoffice' />
      </AuthGate>
    </Box>
  </Box>
);
